import { motion } from "framer-motion";
import { FALLBACK, pick } from "@/lib/gallery";

const misi = [
  "Mencetak generasi muda agar mencintai Al-Qur'an melalui program tahsin dan tahfidz.",
  "Mencetak generasi muda agar memahami bahasa Al-Qur'an melalui program bahasa Arab.",
  "Mencetak generasi muda agar berakhlak Al-Qur'an melalui kajian Al-Qur'an, hadits dan dirosah islamiyyah.",
  "Mencetak generasi muda agar memiliki keterampilan melalui training life skill.",
];

const nilai = [
  { id: "01", title: "Hafizh Mutqin", arabic: "حافظ متقن" },
  { id: "02", title: "Berakhlaqul Karimah", arabic: "متين الخلوق" },
  { id: "03", title: "Berintegritas", arabic: "نزاهة و سلامة" },
  { id: "04", title: "Bermanfaat", arabic: "نافع لغيره" },
  { id: "05", title: "Mandiri", arabic: "قادر على الكسب و العمل" },
];

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function About({ gallery }) {
  const image1 = pick(gallery?.image1, FALLBACK.tentang.image1);
  const image2 = pick(gallery?.image2, FALLBACK.tentang.image2);
  const description = (gallery?.description || "").trim();
  return (
    <section
      id="tentang"
      className="relative bg-parchment py-24 md:py-32"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: sticky image stack */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-4">
              <motion.div
                variants={fade}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                className="relative overflow-hidden rounded-2xl border border-gold/30"
              >
                <img
                  src={image1}
                  alt="Membaca Al-Quran"
                  className="w-full h-[420px] object-cover img-warm"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-forest/85 to-transparent">
                  <p className="font-arabic text-2xl text-parchment">وَنُنَزِّلُ مِنَ الْقُرْآنِ مَا هُوَ شِفَاءٌ</p>
                </div>
              </motion.div>
              <motion.div
                variants={fade}
                custom={1}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                className="relative overflow-hidden rounded-2xl border border-gold/30 ml-12"
              >
                <img
                  src={image2}
                  alt="Interior Masjid"
                  className="w-full h-[240px] object-cover img-warm"
                />
              </motion.div>
            </div>
          </div>

          {/* Right: copy */}
          <div className="lg:col-span-7">
            <motion.span
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-xs tracking-[0.4em] uppercase text-gold"
            >
              Tentang Pondok
            </motion.span>
            <motion.h2
              variants={fade}
              custom={1}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mt-4 font-serif-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.1]"
              data-testid="about-headline"
            >
              Rumah para penghafal,<br />
              <span className="italic text-gold">penjaga warisan langit.</span>
            </motion.h2>

            {description && (
              <motion.p
                variants={fade}
                custom={2}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mt-6 text-moss text-base md:text-lg leading-relaxed max-w-2xl"
                data-testid="about-description"
              >
                {description}
              </motion.p>
            )}

            {/* VISI */}
            <motion.div
              variants={fade}
              custom={2}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="mt-14"
              data-testid="about-visi"
            >
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-2 font-serif-display text-gold text-3xl">01</div>
                <div className="col-span-10">
                  <h3 className="font-serif-display text-forest text-2xl md:text-3xl">Visi</h3>
                  <p className="mt-4 text-moss leading-relaxed text-base md:text-lg">
                    Menjadi Pencetak Generasi Muda Rabbani Yang Berkepribadian Qur&apos;an. Rabbani adalah yang mengenal Allah <span className="font-arabic">ﷻ</span>, paham terhadap syari&apos;at, santun dalam perilaku, menjadi pembimbing manusia yang <em>alim, hakim, halim, mu&apos;alim, murabbi</em>.
                  </p>

                  <div className="mt-6 p-6 rounded-xl bg-sand border border-gold/30">
                    <p className="font-arabic text-2xl md:text-3xl text-forest leading-loose text-right" dir="rtl">
                      وَلَكِنْ كُونُوا رَبَّانِيِّينَ بِمَا كُنْتُمْ تُعَلِّمُونَ الْكِتَابَ وَبِمَا كُنْتُمْ تَدْرُسُونَ
                    </p>
                    <p className="mt-4 text-sm text-moss italic">
                      &ldquo;...hendaklah kalian menjadi robbani, disebabkan kalian mengajarkan kitab dan kalian mengkaji (kitab dan sunah).&rdquo;
                    </p>
                    <p className="mt-2 text-xs tracking-[0.2em] uppercase text-gold">
                      QS. Ali Imran: 79
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* MISI */}
            <motion.div
              variants={fade}
              custom={3}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="mt-14 pt-14 border-t border-gold/20"
              data-testid="about-misi"
            >
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-2 font-serif-display text-gold text-3xl">02</div>
                <div className="col-span-10">
                  <h3 className="font-serif-display text-forest text-2xl md:text-3xl">Misi</h3>
                  <ul className="mt-5 space-y-4">
                    {misi.map((m, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-4 text-moss leading-relaxed"
                        data-testid={`misi-item-${i}`}
                      >
                        <span className="mt-2 inline-flex w-6 h-6 rounded-full bg-forest text-parchment items-center justify-center text-xs flex-shrink-0">
                          {i + 1}
                        </span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* NILAI — full width band */}
        <motion.div
          variants={fade}
          custom={4}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-24 pt-16 border-t border-gold/20"
          data-testid="about-nilai"
        >
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">5 Nilai Luhur Pondok</span>
            <p className="mt-2 font-arabic text-2xl md:text-3xl text-forest" dir="rtl">
              خمس قيم المعهد
            </p>
            <h3 className="mt-4 font-serif-display text-forest text-3xl md:text-4xl lg:text-5xl leading-tight">
              Lima nilai yang menjadi<br />
              <span className="italic text-gold">denyut nadi setiap santri.</span>
            </h3>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {nilai.map((n, i) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="group relative p-6 rounded-2xl border border-gold/30 bg-sand hover:bg-forest hover:text-parchment transition-colors duration-500 lift"
                data-testid={`nilai-${i}`}
              >
                <span className="font-serif-display text-gold text-2xl">{n.id}</span>
                <h4 className="mt-4 font-serif-display text-xl md:text-2xl leading-tight">
                  {n.title}
                </h4>
                <p className="mt-3 font-arabic text-xl md:text-2xl text-gold leading-loose" dir="rtl">
                  {n.arabic}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
